import type { TenantAuditEventSummary } from '@nadoutong/contracts';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenantAuditEvents } from '../../api/useOrgs.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { formatTimestamp } from '../../components/formatters.ts';
import { hasPermission, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Panel,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { formatTeamApiError } from './shared.ts';

/**
 * `/team/audit` — tenant audit log as its own routed page (epic #897
 * PR 7). The audit query fires only here (the old mega-page fetched it
 * lazily per tab — routing preserves that efficiency for free).
 *
 * Filters auto-apply with the PR 6 semantic: the two text inputs
 * debounce 300ms into URL search params (`action`, `target`; `replace`
 * so typing does not spam history), so filtered views deep-link.
 * Pagination is derived from the server `limit`: a full page implies a
 * next page (the endpoint reports no total).
 */

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 300;

export function TeamAudit(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const orgId = user?.orgId ?? undefined;
  const canReadAudit =
    hasPermission(user, 'org:audit:read') || hasPermission(user, 'member:audit:read');

  const [searchParams, setSearchParams] = useSearchParams();
  const action = searchParams.get('action') ?? '';
  const target = searchParams.get('target') ?? '';

  const [actionInput, setActionInput] = useState(action);
  const [targetInput, setTargetInput] = useState(target);
  const [offset, setOffset] = useState(0);

  function patchParams(patch: Readonly<Record<string, string | null>>): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value.length === 0) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
    setOffset(0);
  }

  // Keep inputs in sync when the URL changes from outside (deep link,
  // clear button); the debounce below only fires when values differ.
  useEffect(() => {
    setActionInput(action);
  }, [action]);
  useEffect(() => {
    setTargetInput(target);
  }, [target]);

  useEffect(() => {
    const trimmedAction = actionInput.trim();
    const trimmedTarget = targetInput.trim();
    if (trimmedAction === action && trimmedTarget === target) return undefined;
    const timer = setTimeout(() => {
      patchParams({ action: trimmedAction, target: trimmedTarget });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [actionInput, targetInput, action, target]);

  const auditQ = useTenantAuditEvents(
    canReadAudit ? orgId : undefined,
    {
      limit: PAGE_SIZE,
      offset,
      action: action.length > 0 ? action : undefined,
      targetAccountId: target.length > 0 ? target : undefined,
    },
    canReadAudit,
  );

  if (!canReadAudit) {
    return (
      <section data-testid="team-audit-page" className="flex flex-col gap-4">
        <PageHeader title={t('team.audit.title')} testId="team-audit-header" />
        <Panel caption={t('team.audit.events.title')} padded>
          <p className="m-0 text-dense text-paper-low" data-testid="team-audit-forbidden">
            {t('team.audit.forbidden')}
          </p>
        </Panel>
      </section>
    );
  }

  const events = auditQ.data?.events ?? [];
  const limit = auditQ.data?.limit ?? PAGE_SIZE;
  const page = Math.floor(offset / Math.max(1, limit)) + 1;
  const hasNext = events.length >= limit;
  const pageCount = hasNext ? page + 1 : page;
  const hasFilter = action.length > 0 || target.length > 0;

  return (
    <section data-testid="team-audit-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('team.audit.title')}
        sub={t('team.audit.subtitle')}
        testId="team-audit-header"
      />

      <Panel caption={t('team.audit.filter.caption')} padded testId="team-audit-filter-bar">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            mono
            value={actionInput}
            onChange={(event) => setActionInput(event.currentTarget.value)}
            placeholder={t('team.audit.filter.action_placeholder')}
            aria-label={t('team.audit.filter.action_label')}
            className="w-96 max-w-full"
            data-testid="team-audit-action-filter"
          />
          <Input
            mono
            value={targetInput}
            onChange={(event) => setTargetInput(event.currentTarget.value)}
            placeholder={t('team.audit.filter.target_placeholder')}
            aria-label={t('team.audit.filter.target_label')}
            className="w-64 max-w-full"
            data-testid="team-audit-target-filter"
          />
          {hasFilter ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patchParams({ action: null, target: null })}
              data-testid="team-audit-clear-filters"
            >
              {t('team.audit.filter.clear')}
            </Button>
          ) : null}
        </div>
      </Panel>

      <Panel caption={t('team.audit.events.title')} testId="team-audit-events">
        {auditQ.isLoading ? (
          <TableSkeleton rows={5} testId="team-audit-loading" />
        ) : auditQ.isError ? (
          <div className="p-4">
            <PanelError
              message={formatTeamApiError(auditQ.error, t('team.audit.error.load'))}
              retryLabel={t('team.retry')}
              onRetry={() => {
                void auditQ.refetch();
              }}
              testId="team-audit-error"
            />
          </div>
        ) : events.length === 0 ? (
          <EmptyState testId="team-audit-empty" title={t('team.audit.empty')} />
        ) : (
          <Table className="rounded-none border-0" testId="team-audit-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('team.audit.column.time')}</Th>
                <Th>{t('team.audit.column.actor')}</Th>
                <Th>{t('team.audit.column.action')}</Th>
                <Th>{t('team.audit.column.target')}</Th>
                <Th>{t('team.audit.column.detail')}</Th>
              </Tr>
            </THead>
            <TBody>
              {events.map((event) => (
                <Tr key={event.id} data-testid={`team-audit-row-${event.id}`}>
                  <Td mono heading>
                    {formatTimestamp(event.createdAt)}
                  </Td>
                  <Td mono label={t('team.audit.column.actor')}>
                    {event.actorAccountId ?? t('team.audit.actor_system')}
                  </Td>
                  <Td label={t('team.audit.column.action')}>
                    <Chip
                      tone="signal"
                      lamp={false}
                      className="font-mono"
                      testId={`team-audit-action-${event.id}`}
                    >
                      {event.action}
                    </Chip>
                  </Td>
                  <Td mono label={t('team.audit.column.target')}>
                    {event.targetAccountId ?? '--'}
                  </Td>
                  <Td label={t('team.audit.column.detail')} stack>
                    <span className="break-words font-mono text-micro text-paper-low">
                      {formatAuditMetadata(event.metadata)}
                    </span>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
        <div className="flex items-center justify-end border-t border-line-soft px-4 py-3">
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(next) => setOffset((next - 1) * limit)}
            prevLabel={t('team.audit.pagination.previous')}
            nextLabel={t('team.audit.pagination.next')}
            ariaLabel={t('team.audit.pagination.aria')}
            testId="team-audit-pagination"
          />
        </div>
      </Panel>
    </section>
  );
}

function formatAuditMetadata(metadata: TenantAuditEventSummary['metadata']): string {
  const entries = Object.entries(metadata);
  if (entries.length === 0) return '--';
  return entries
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${formatAuditValue(value)}`)
    .join(' · ');
}

function formatAuditValue(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.length}]`;
  }
  if (typeof value === 'object') {
    return '{...}';
  }
  return String(value);
}
